import React, { useState, useEffect, useCallback } from 'react';
import { 
  Search, ShoppingCart, Eye, Plus, X, Star, Clock, 
  Camera, Music, Video, Mic, Headphones, Palette, 
  Shirt, PenTool, Film, ImageIcon, Disc, Briefcase,
  Settings, Trash2, Zap
} from 'lucide-react';
import { toast } from 'sonner';
import { useUserId } from '../context/UserAuthContext';

const API_URL = window.location.origin;

const CreativeServices = () => {
  const userId = useUserId();
  
  // State
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [cart, setCart] = useState({ items: [], total: 0 });
  const [showCart, setShowCart] = useState(false);
  const [showServiceModal, setShowServiceModal] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [showNewService, setShowNewService] = useState(false);
  const [showManageCategories, setShowManageCategories] = useState(false);

  // Creative service categories with icons
  const categoryConfig = {
    'All': { icon: Star, color: 'bg-gray-600' },
    'Photography': { icon: Camera, color: 'bg-red-600' },
    'Music & Producing': { icon: Music, color: 'bg-purple-600' },
    'Videography': { icon: Video, color: 'bg-orange-600' },
    'Podcasting & Streaming': { icon: Mic, color: 'bg-pink-600' },
    'Audio Engineering': { icon: Headphones, color: 'bg-orange-500' },
    'Graphic Design': { icon: Palette, color: 'bg-amber-700' },
    'Fashion Styling': { icon: Shirt, color: 'bg-gray-600' },
    'Writing & Copywriting': { icon: PenTool, color: 'bg-blue-900' },
    'Animation & Motion Design': { icon: Film, color: 'bg-indigo-600' },
    'Visual Art & Illustration': { icon: ImageIcon, color: 'bg-cyan-600' },
    'DJing & Performing': { icon: Disc, color: 'bg-fuchsia-600' },
    'Entrepreneurship & Branding': { icon: Briefcase, color: 'bg-emerald-600' }
  };

  // Fetch services
  const fetchServices = useCallback(async () => {
    try {
      let url = `${API_URL}/api/services/?category=creative`;
      if (selectedCategory !== 'All') {
        url += `&subcategory=${encodeURIComponent(selectedCategory)}`;
      }
      
      const res = await fetch(url);
      let data = await res.json();
      
      // Apply search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        data = data.filter(s => 
          s.name.toLowerCase().includes(query) ||
          s.subcategory.toLowerCase().includes(query) ||
          s.tagline.toLowerCase().includes(query)
        );
      }
      
      setServices(data);
    } catch (error) {
      console.error('Error fetching services:', error);
    }
  }, [selectedCategory, searchQuery]);

  // Fetch categories
  const fetchCategories = async () => {
    try {
      const res = await fetch(`${API_URL}/api/services/categories`);
      const data = await res.json();
      
      // Get creative subcategories
      const creativeSubcats = data.category_map?.creative || [];
      setCategories(['All', ...creativeSubcats]);
    } catch (error) {
      console.error('Error fetching categories:', error);
      // Fallback categories
      setCategories([
        'All', 'Photography', 'Music & Producing', 'Videography',
        'Podcasting & Streaming', 'Audio Engineering', 'Graphic Design',
        'Fashion Styling', 'Writing & Copywriting', 'Animation & Motion Design',
        'Visual Art & Illustration', 'DJing & Performing', 'Entrepreneurship & Branding'
      ]);
    }
  };

  // Fetch cart
  const fetchCart = async () => {
    if (!userId) return;
    try {
      const res = await fetch(`${API_URL}/api/cart/${userId}`);
      const data = await res.json();
      setCart(data);
    } catch (error) {
      console.error('Error fetching cart:', error);
    }
  };

  // Seed services if empty
  const seedServices = async () => {
    try {
      const res = await fetch(`${API_URL}/api/services/seed`, { method: 'POST' });
      const data = await res.json();
      toast.success(`Loaded ${data.creative} creative services`);
      fetchServices();
      fetchCategories();
    } catch (error) {
      console.error('Error seeding services:', error);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchServices(), fetchCategories(), fetchCart()]);
      setLoading(false);
    };
    loadData();
  }, [fetchServices]);

  // Auto-seed if no services
  useEffect(() => {
    if (!loading && services.length === 0) {
      seedServices();
    }
  }, [loading, services.length]);

  // Add to cart
  const addToCart = async (service) => {
    if (!userId) {
      toast.error('Please log in to add items to cart');
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/cart/${userId}/add`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          service_id: service.id,
          service_name: service.name,
          base_price: service.base_price,
          quantity: 1,
          selected_addons: [],
          addon_total: 0
        })
      });
      
      const data = await res.json();
      if (data.message) {
        toast.success('Added to cart');
        fetchCart();
      }
    } catch (error) {
      toast.error('Failed to add to cart');
    }
  };

  // Remove from cart
  const removeFromCart = async (serviceId) => {
    try {
      const res = await fetch(`${API_URL}/api/cart/${userId}/remove/${serviceId}`, {
        method: 'DELETE'
      });
      
      const data = await res.json();
      if (data.message) {
        toast.success('Removed from cart');
        fetchCart();
      }
    } catch (error) {
      toast.error('Failed to remove from cart');
    }
  };

  // View service details
  const viewService = (service) => {
    setSelectedService(service);
    setShowServiceModal(true);
  };

  // Get category color
  const getCategoryColor = (subcategory) => {
    return categoryConfig[subcategory]?.color || 'bg-gray-600';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-white">Loading creative services...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-white">Creative Services</h1>
            <p className="text-gray-400 mt-1">Professional creative services for your business</p>
          </div>
          
          {/* Toolbar actions */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowNewService(true)}
              className="flex items-center gap-2 px-4 py-2 bg-white text-black rounded-lg hover:bg-gray-100 transition-colors text-sm font-medium"
              data-testid="new-service-btn"
            >
              <Plus size={18} />
              New Service
            </button>
            <button
              onClick={() => setShowManageCategories(true)}
              className="flex items-center gap-2 px-4 py-2 bg-black hover:bg-gray-800 text-white border border-gray-700 rounded-lg transition-colors text-sm font-medium"
              data-testid="manage-categories-btn"
            >
              <Settings size={18} />
              Manage Categories
            </button>
            <button
              onClick={() => setShowCart(true)}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
              data-testid="cart-btn"
            >
              <ShoppingCart size={20} />
              Cart
              {cart.items?.length > 0 && (
                <span className="ml-1 px-2 py-0.5 bg-white text-red-600 rounded-full text-xs font-bold">
                  {cart.items.length}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Search Bar */}
        <div className="bg-black border border-gray-400 rounded-xl hover:border-gray-300 hover:shadow-[0_0_20px_rgba(156,163,175,0.5)] transition-all duration-300 p-4 mb-6">
          <div className="relative">
            <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search services..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-black border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-blue-500"
            />
          </div>
        </div>

        {/* Category Tabs */}
        <div className="flex flex-wrap gap-2 mb-6">
          {categories.map(cat => {
            const config = categoryConfig[cat] || { icon: Star, color: 'bg-gray-600' };
            const isActive = selectedCategory === cat;
            
            return (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                  isActive 
                    ? 'bg-white text-black' 
                    : 'bg-black text-gray-300 hover:bg-gray-800 border border-gray-700'
                }`}
              >
                {cat}
              </button>
            );
          })}
        </div>

        {/* Services Grid */}
        {services.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            <Briefcase size={48} className="mx-auto mb-4 opacity-50" />
            <p>No services found. Loading catalog...</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {services.map(service => (
              <div 
                key={service.id} 
                className="bg-black border border-gray-400 rounded-xl overflow-hidden hover:border-gray-300 hover:shadow-[0_0_20px_rgba(156,163,175,0.5)] transition-all duration-300"
                data-testid={`service-card-${service.id}`}
              >
                {/* Category Badge */}
                <div className="p-4 pb-0">
                  <span className={`inline-block px-3 py-1 ${getCategoryColor(service.subcategory)} text-white text-xs font-medium rounded`}>
                    {service.subcategory}
                  </span>
                </div>
                
                {/* Content */}
                <div className="p-4">
                  <h3 className="text-white font-semibold text-lg mb-2 line-clamp-2">
                    {service.name}
                  </h3>
                  <p className="text-gray-400 text-sm mb-4 line-clamp-2">
                    {service.tagline}
                  </p>
                  
                  {/* Price & Timeline */}
                  <div className="mb-4">
                    <div className="flex items-baseline gap-1">
                      <span className="text-2xl font-bold text-white">${service.base_price}</span>
                      <span className="text-gray-400 text-sm">{service.pricing_unit}</span>
                    </div>
                    <div className="flex items-center gap-1 text-gray-500 text-sm mt-1">
                      <Clock size={14} />
                      {service.timeline}
                    </div>
                  </div>
                  
                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => viewService(service)}
                      className="flex-1 flex items-center justify-center gap-2 py-2 bg-black border border-gray-700 text-white rounded-lg hover:bg-gray-900 transition-colors text-sm"
                    >
                      <Eye size={16} />
                      View Details
                    </button>
                    <button
                      onClick={() => addToCart(service)}
                      className="p-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
                      title="Add to cart"
                    >
                      <ShoppingCart size={18} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Service Detail Modal */}
        {showServiceModal && selectedService && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-gray-900 border border-gray-700 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-700 flex items-center justify-between">
                <div>
                  <span className={`inline-block px-3 py-1 ${getCategoryColor(selectedService.subcategory)} text-white text-xs font-medium rounded mb-2`}>
                    {selectedService.subcategory}
                  </span>
                  <h2 className="text-xl font-bold text-white">{selectedService.name}</h2>
                </div>
                <button onClick={() => setShowServiceModal(false)} className="text-gray-400 hover:text-white">
                  <X size={24} />
                </button>
              </div>
              
              <div className="p-6">
                <p className="text-gray-300 mb-6">{selectedService.description}</p>
                
                {/* Price */}
                <div className="bg-black rounded-lg p-4 mb-6">
                  <div className="flex items-baseline gap-2">
                    <span className="text-3xl font-bold text-white">${selectedService.base_price}</span>
                    <span className="text-gray-400">{selectedService.pricing_unit}</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-400 mt-2">
                    <Clock size={16} />
                    <span>Delivery: {selectedService.timeline}</span>
                  </div>
                </div>
                
                {/* Deliverables */}
                {selectedService.deliverables?.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-white font-semibold mb-3">What's Included</h3>
                    <ul className="space-y-2">
                      {selectedService.deliverables.map((d, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-gray-300">
                          <span className="text-green-500 mt-1">✓</span>
                          {d.item}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {/* Add-ons */}
                {selectedService.addons?.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-white font-semibold mb-3">Available Add-ons</h3>
                    <div className="space-y-2">
                      {selectedService.addons.map((addon, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-black rounded-lg">
                          <div>
                            <p className="text-white">{addon.name}</p>
                            <p className="text-gray-500 text-sm">{addon.description}</p>
                          </div>
                          <span className="text-white font-medium">+${addon.price}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* FAQs */}
                {selectedService.faqs?.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-white font-semibold mb-3">FAQs</h3>
                    <div className="space-y-3">
                      {selectedService.faqs.map((faq, idx) => (
                        <div key={idx} className="p-3 bg-black rounded-lg">
                          <p className="text-white font-medium mb-1">{faq.question}</p>
                          <p className="text-gray-400 text-sm">{faq.answer}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Action Buttons */}
                <div className="flex gap-3">
                  <button
                    onClick={() => { addToCart(selectedService); setShowServiceModal(false); }}
                    className="flex-1 flex items-center justify-center gap-2 py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors font-medium"
                  >
                    <ShoppingCart size={18} />
                    Add to Cart
                  </button>
                  <button
                    onClick={() => setShowServiceModal(false)}
                    className="px-6 py-3 bg-gray-800 hover:bg-gray-700 text-white rounded-lg transition-colors"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Cart Sidebar */}
        {showCart && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex justify-end">
            <div className="w-full max-w-md bg-gray-900 border-l border-gray-700 h-full overflow-y-auto">
              <div className="p-6 border-b border-gray-700 flex items-center justify-between">
                <h2 className="text-xl font-bold text-white flex items-center gap-2">
                  <ShoppingCart size={24} />
                  Your Cart
                </h2>
                <button onClick={() => setShowCart(false)} className="text-gray-400 hover:text-white">
                  <X size={24} />
                </button>
              </div>
              
              <div className="p-6">
                {(!cart.items || cart.items.length === 0) ? (
                  <div className="text-center py-12 text-gray-400">
                    <ShoppingCart size={48} className="mx-auto mb-4 opacity-50" />
                    <p>Your cart is empty</p>
                    <button
                      onClick={() => setShowCart(false)}
                      className="mt-4 px-6 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg transition-colors"
                    >
                      Browse Services
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="space-y-4 mb-6">
                      {cart.items.map((item, idx) => (
                        <div key={idx} className="bg-black rounded-lg p-4">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <h4 className="text-white font-medium">{item.service_name}</h4>
                              <p className="text-gray-400 text-sm mt-1">Qty: {item.quantity}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-white font-medium">${item.base_price * item.quantity}</p>
                              <button
                                onClick={() => removeFromCart(item.service_id)}
                                className="text-red-400 hover:text-red-300 text-sm mt-1"
                              >
                                Remove
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    {/* Cart Total */}
                    <div className="border-t border-gray-700 pt-4">
                      <div className="flex items-center justify-between text-lg">
                        <span className="text-gray-400">Total</span>
                        <span className="text-white font-bold">${cart.total || cart.subtotal || 0}</span>
                      </div>
                      
                      <div className="space-y-3 mt-6">
                        <button
                          onClick={() => toast.info('Request Quote feature coming soon')}
                          className="w-full py-3 bg-white text-black rounded-lg hover:bg-gray-100 transition-colors font-medium"
                        >
                          Request Quote
                        </button>
                        <button
                          onClick={() => toast.info('Book a Call feature coming soon')}
                          className="w-full py-3 bg-gray-800 text-white rounded-lg hover:bg-gray-700 transition-colors font-medium"
                        >
                          Book a Call
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
            
            {/* Click outside to close */}
            <div className="flex-1" onClick={() => setShowCart(false)} />
          </div>
        )}
        
        {/* New Service Modal */}
        {showNewService && (
          <NewServiceModal
            categories={categories.filter(c => c !== 'All')}
            onClose={() => setShowNewService(false)}
            onCreated={() => {
              setShowNewService(false);
              fetchServices();
              fetchCategories();
            }}
          />
        )}
        
        {/* Manage Categories / Keywords Modal */}
        {showManageCategories && (
          <ManageCategoriesModal
            onClose={() => setShowManageCategories(false)}
            onChanged={() => {
              fetchServices();
              fetchCategories();
            }}
          />
        )}
      </div>
    </div>
  );
};

// ========== New Service Modal ==========
const NewServiceModal = ({ categories, onClose, onCreated }) => {
  const [name, setName] = useState('');
  const [subcategory, setSubcategory] = useState(categories[0] || '');
  const [tagline, setTagline] = useState('');
  const [description, setDescription] = useState('');
  const [basePrice, setBasePrice] = useState('');
  const [pricingUnit, setPricingUnit] = useState('starting at');
  const [timeline, setTimeline] = useState('1-2 weeks');
  const [deliverablesText, setDeliverablesText] = useState('');
  const [color, setColor] = useState('bg-purple-600');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error('Name is required'); return; }
    if (!subcategory) { toast.error('Category is required'); return; }
    const price = parseFloat(basePrice);
    if (isNaN(price) || price < 0) { toast.error('Enter a valid price'); return; }
    
    setSubmitting(true);
    try {
      const deliverables = deliverablesText
        .split('\n').map(s => s.trim()).filter(Boolean)
        .map(item => ({ item }));
      
      const payload = {
        name: name.trim(),
        category: 'creative',
        subcategory,
        tagline: tagline.trim(),
        description: description.trim(),
        deliverables,
        timeline,
        base_price: price,
        pricing_unit: pricingUnit,
        addons: [],
        faqs: [],
        color,
        is_active: true,
      };
      
      const res = await fetch(`${API_URL}/api/services/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (res.ok && data.id) {
        toast.success('Service created');
        onCreated();
      } else {
        toast.error(data.detail || 'Failed to create service');
      }
    } catch (err) {
      toast.error('Failed to create service');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={onClose} data-testid="new-service-modal">
      <div className="bg-black border border-gray-400 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-800 sticky top-0 bg-black z-10">
          <h3 className="text-white text-lg font-semibold">New Service</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white" data-testid="close-new-service"><X size={20} /></button>
        </div>
        
        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-gray-400 mb-1.5">Service Name *</label>
              <input type="text" value={name} onChange={e => setName(e.target.value)}
                className="w-full px-3 py-2 bg-black border border-gray-700 rounded-lg text-white text-sm"
                placeholder="e.g. Brand Photo Shoot" required data-testid="service-name-input" />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1.5">Category *</label>
              <select value={subcategory} onChange={e => setSubcategory(e.target.value)}
                className="w-full px-3 py-2 bg-black border border-gray-700 rounded-lg text-white text-sm"
                data-testid="service-category-select">
                {categories.map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
          </div>
          
          <div>
            <label className="block text-xs text-gray-400 mb-1.5">Tagline</label>
            <input type="text" value={tagline} onChange={e => setTagline(e.target.value)}
              className="w-full px-3 py-2 bg-black border border-gray-700 rounded-lg text-white text-sm"
              placeholder="One-line hook" data-testid="service-tagline-input" />
          </div>
          
          <div>
            <label className="block text-xs text-gray-400 mb-1.5">Description</label>
            <textarea value={description} onChange={e => setDescription(e.target.value)} rows={3}
              className="w-full px-3 py-2 bg-black border border-gray-700 rounded-lg text-white text-sm resize-none"
              placeholder="What's included, who it's for..." data-testid="service-description-input" />
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs text-gray-400 mb-1.5">Price *</label>
              <input type="number" min="0" step="0.01" value={basePrice} onChange={e => setBasePrice(e.target.value)}
                className="w-full px-3 py-2 bg-black border border-gray-700 rounded-lg text-white text-sm"
                placeholder="0.00" required data-testid="service-price-input" />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1.5">Pricing Unit</label>
              <select value={pricingUnit} onChange={e => setPricingUnit(e.target.value)}
                className="w-full px-3 py-2 bg-black border border-gray-700 rounded-lg text-white text-sm">
                <option>starting at</option>
                <option>per session</option>
                <option>per hour</option>
                <option>per project</option>
                <option>per month</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1.5">Timeline</label>
              <input type="text" value={timeline} onChange={e => setTimeline(e.target.value)}
                className="w-full px-3 py-2 bg-black border border-gray-700 rounded-lg text-white text-sm"
                placeholder="1-2 weeks" />
            </div>
          </div>
          
          <div>
            <label className="block text-xs text-gray-400 mb-1.5">Deliverables (one per line)</label>
            <textarea value={deliverablesText} onChange={e => setDeliverablesText(e.target.value)} rows={4}
              className="w-full px-3 py-2 bg-black border border-gray-700 rounded-lg text-white text-sm resize-none"
              placeholder={'10 edited photos\nHigh-res downloads\nCommercial usage rights'} />
          </div>
          
          <div>
            <label className="block text-xs text-gray-400 mb-1.5">Card Color</label>
            <select value={color} onChange={e => setColor(e.target.value)}
              className="w-full px-3 py-2 bg-black border border-gray-700 rounded-lg text-white text-sm">
              <option value="bg-red-600">Red</option>
              <option value="bg-orange-600">Orange</option>
              <option value="bg-amber-700">Amber</option>
              <option value="bg-emerald-600">Emerald</option>
              <option value="bg-blue-900">Blue</option>
              <option value="bg-cyan-600">Cyan</option>
              <option value="bg-purple-600">Purple</option>
              <option value="bg-indigo-600">Indigo</option>
              <option value="bg-pink-600">Pink</option>
              <option value="bg-fuchsia-600">Fuchsia</option>
              <option value="bg-gray-600">Gray</option>
            </select>
          </div>
          
          <div className="flex justify-end gap-3 pt-3 border-t border-gray-800">
            <button type="button" onClick={onClose} className="px-4 py-2 text-gray-300 hover:text-white" data-testid="cancel-new-service">Cancel</button>
            <button type="submit" disabled={submitting}
              className="px-5 py-2 bg-white text-black rounded-lg font-medium hover:bg-gray-100 disabled:opacity-50"
              data-testid="submit-new-service">
              {submitting ? 'Creating…' : 'Create Service'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ========== Manage Categories / Keywords Modal ==========
const ManageCategoriesModal = ({ onClose, onChanged }) => {
  const [keywordRows, setKeywordRows] = useState([]);  // [{subcategory, category, keywords: []}]
  const [loading, setLoading] = useState(true);
  const [scanResult, setScanResult] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [newSubcat, setNewSubcat] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/services/category-keywords?category=creative`);
      const data = await res.json();
      setKeywordRows(Array.isArray(data) ? data : []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const updateKeywords = (subcategory, keywordsText) => {
    setKeywordRows(prev => prev.map(r =>
      r.subcategory === subcategory
        ? { ...r, keywords: keywordsText.split(',').map(s => s.trim()).filter(Boolean) }
        : r
    ));
  };

  const saveRow = async (row) => {
    try {
      const res = await fetch(`${API_URL}/api/services/category-keywords`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ subcategory: row.subcategory, category: row.category || 'creative', keywords: row.keywords || [] }),
      });
      const data = await res.json();
      if (data.success) {
        toast.success(`Saved ${row.subcategory} (${data.keyword_count} keywords)`);
      } else {
        toast.error('Failed to save');
      }
    } catch {
      toast.error('Failed to save');
    }
  };

  const deleteRow = async (subcategory) => {
    if (!window.confirm(`Delete keywords for "${subcategory}"?`)) return;
    try {
      await fetch(`${API_URL}/api/services/category-keywords/${encodeURIComponent(subcategory)}`, { method: 'DELETE' });
      setKeywordRows(prev => prev.filter(r => r.subcategory !== subcategory));
      toast.success(`Deleted "${subcategory}"`);
    } catch {
      toast.error('Failed to delete');
    }
  };

  const addSubcat = () => {
    const name = newSubcat.trim();
    if (!name) return;
    if (keywordRows.some(r => r.subcategory === name)) { toast.error('Already exists'); return; }
    setKeywordRows(prev => [...prev, { subcategory: name, category: 'creative', keywords: [] }]);
    setNewSubcat('');
  };

  const runScan = async (dryRun = true) => {
    setScanning(true);
    try {
      const res = await fetch(`${API_URL}/api/services/scan-keywords?category=creative&dry_run=${dryRun}`, { method: 'POST' });
      const data = await res.json();
      setScanResult(data);
      if (dryRun) {
        toast.info(`Scanned ${data.scanned} — ${data.changes?.length || 0} would be reassigned`);
      } else {
        toast.success(`Reassigned ${data.reassigned} services`);
        if (onChanged) onChanged();
      }
    } catch {
      toast.error('Scan failed');
    }
    setScanning(false);
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={onClose} data-testid="manage-categories-modal">
      <div className="bg-black border border-gray-400 rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-800 sticky top-0 bg-black z-10">
          <h3 className="text-white text-lg font-semibold">Manage Category Keywords</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white" data-testid="close-manage-categories"><X size={20} /></button>
        </div>
        
        <div className="px-6 py-5 space-y-4">
          <p className="text-sm text-gray-400">
            Add keywords for each category. Click <strong>Scan Keywords</strong> to auto-assign existing services
            to the category whose keywords match most strongly in their name / tagline / description.
          </p>
          
          {/* Add new subcategory */}
          <div className="flex gap-2">
            <input
              type="text"
              value={newSubcat}
              onChange={e => setNewSubcat(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addSubcat(); } }}
              placeholder="Add new category (e.g. Live Performance)"
              className="flex-1 px-3 py-2 bg-black border border-gray-700 rounded-lg text-white text-sm"
              data-testid="new-subcat-input"
            />
            <button onClick={addSubcat}
              className="px-4 py-2 bg-white text-black rounded-lg text-sm font-medium hover:bg-gray-100"
              data-testid="add-subcat-btn">
              + Add
            </button>
          </div>
          
          {/* Keyword table */}
          {loading ? (
            <div className="py-6 text-center text-gray-400">Loading…</div>
          ) : keywordRows.length === 0 ? (
            <div className="py-8 text-center text-gray-400 border border-dashed border-gray-700 rounded-lg">
              No category keywords set yet. Add one above to get started.
            </div>
          ) : (
            <div className="space-y-2">
              {keywordRows.map(row => (
                <div key={row.subcategory} className="p-3 bg-black/30 border border-gray-800 rounded-lg" data-testid={`keyword-row-${row.subcategory}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-white font-medium text-sm">{row.subcategory}</span>
                    <div className="flex items-center gap-2">
                      <button onClick={() => saveRow(row)}
                        className="text-xs px-3 py-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded">Save</button>
                      <button onClick={() => deleteRow(row.subcategory)}
                        className="text-gray-400 hover:text-red-400"><Trash2 size={14} /></button>
                    </div>
                  </div>
                  <input
                    type="text"
                    defaultValue={(row.keywords || []).join(', ')}
                    onBlur={e => updateKeywords(row.subcategory, e.target.value)}
                    placeholder="photo, camera, headshot, editorial (comma-separated)"
                    className="w-full px-3 py-2 bg-black border border-gray-700 rounded-lg text-white text-sm"
                  />
                </div>
              ))}
            </div>
          )}
          
          {/* Scan section */}
          <div className="pt-4 border-t border-gray-800">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-white font-medium">Scan & Auto-Categorize</h4>
              <div className="flex items-center gap-2">
                <button onClick={() => runScan(true)} disabled={scanning}
                  className="px-4 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg text-sm disabled:opacity-50"
                  data-testid="scan-preview-btn">
                  {scanning ? 'Scanning…' : 'Preview Changes'}
                </button>
                <button onClick={() => runScan(false)} disabled={scanning}
                  className="flex items-center gap-2 px-4 py-2 bg-yellow-500 text-black rounded-lg text-sm font-medium hover:bg-yellow-400 disabled:opacity-50"
                  data-testid="scan-apply-btn">
                  <Zap size={16} />
                  Apply Auto-Categorize
                </button>
              </div>
            </div>
            {scanResult && (
              <div className="bg-black/40 border border-gray-800 rounded-lg p-3 text-sm">
                <div className="text-gray-300 mb-2">
                  Scanned <strong className="text-white">{scanResult.scanned}</strong> services •
                  Reassigned <strong className="text-white">{scanResult.reassigned}</strong>
                  {scanResult.dry_run && <span className="ml-2 text-yellow-400">(preview only)</span>}
                </div>
                {(scanResult.changes || []).length > 0 && (
                  <div className="max-h-40 overflow-y-auto space-y-1">
                    {scanResult.changes.slice(0, 50).map(ch => (
                      <div key={ch.service_id} className="text-xs text-gray-400">
                        <span className="text-white">{ch.service_name}</span>
                        : <span className="text-red-400">{ch.from || '—'}</span> → <span className="text-emerald-400">{ch.to}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreativeServices;
